import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  useSEO({ robots: decideRobots({ pageType: "auth" }), ...buildPageMeta("auth") });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("reset.too_short")); return; }
    if (password !== confirm) { setError(t("reset.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="reset-no-token">
        <AlertTriangle size={40} className="mx-auto text-[#8E4A2E]" strokeWidth={1.4}/>
        <h1 className="mt-4 text-2xl font-bold">{t("reset.invalid_title")}</h1>
        <Link to="/forgot-password" className="mt-4 inline-block text-[#5B8C66] font-bold">{t("reset.request_new")}</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="reset-success">
        <CheckCircle2 size={48} className="mx-auto text-[#5B8C66]" strokeWidth={1.4}/>
        <h1 className="mt-4 text-2xl font-bold">{t("reset.success_title")}</h1>
        <p className="mt-2 text-sm text-[#5F6B61]">{t("reset.success_body")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
      <p className="eyebrow">{t("reset.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{t("reset.title")}</h1>
      <p className="mt-2 text-sm text-[#5F6B61]">{t("reset.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4 card-clay p-7" data-testid="reset-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{t("reset.new_password")}</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white" data-testid="reset-password"/>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{t("reset.confirm_password")}</label>
          <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white" data-testid="reset-confirm"/>
        </div>
        {error && <div className="text-sm text-[#3F6147] bg-[#E9F0E0] border border-[#5B8C66]/20 rounded-xl px-4 py-3" data-testid="reset-error">{error}</div>}
        <button type="submit" disabled={loading} className="btn-pill w-full px-5 py-3 bg-[#5B8C66] text-white font-bold hover:bg-[#3F6147] transition disabled:opacity-60" data-testid="reset-submit">
          {loading ? t("reset.submitting") : t("reset.submit")}
        </button>
      </form>
    </div>
  );
}
